import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FormControl, IconButton, InputAdornment, OutlinedInput, TableCell } from '@mui/material'
import { FaTimes } from 'react-icons/fa'
import FilterOperatorSelectMUI from '../../../filters/FilterOperatorSelectMUI'
import { getDefaultOperatorForType } from '../../../../../utils/filters/filterOperators'

const getInputType = (type, operator) => {
    if (type === 'DateTime' || type === 'Date') {
        return operator === 'CONTAINS' ? 'text' : 'date'
    }
    switch (type) {
        case 'String':
            return 'text'
        case 'Link':
            return 'number'
        case 'Currency':
        case 'Number':
            return 'number'
        default:
            return 'text'
    }
}

const SearchField = ({ column }) => {
    const inputRef = useRef(null)
    const input2Ref = useRef(null)
    const fieldType = column.fieldType || 'string'

    const initOperator =
        column.filterValue && typeof column.filterValue === 'object'
            ? column.filterValue.operator
            : getDefaultOperatorForType(fieldType)
    const initValue =
        column.filterValue && typeof column.filterValue === 'object'
            ? column.filterValue.value || ''
            : ''
    const initValue2 =
        column.filterValue && typeof column.filterValue === 'object'
            ? column.filterValue.value2 || ''
            : ''

    const [operator, setOperator] = useState(initOperator)
    const [value, setValue] = useState(initValue)
    const [value2, setValue2] = useState(initValue2)

    const searchType = getInputType(column.type, operator)

    const updateFilter = useCallback(
        (op, v1, v2) => {
            if (op !== 'BLANK' && !v1 && v1 !== 0) {
                column.setFilter(undefined)
                return
            }
            column.setFilter({
                operator: op,
                value: v1,
                value2: v2 || undefined,
                fieldType,
            })
        },
        [column, fieldType]
    )

    const clearFilter = useCallback(() => {
        inputRef.current.value = null
        if (input2Ref.current) input2Ref.current.value = null
        setValue('')
        setValue2('')
        column.setFilter(undefined)
    }, [column, fieldType])

    useEffect(() => {
        if (!column.filterValue) {
            inputRef.current.value = ''
            if (input2Ref.current) input2Ref.current.value = ''
            setValue('')
            setValue2('')
        }
    }, [column.filterValue, fieldType])

    const onOperatorChange = useCallback(
        (op) => {
            setOperator(op)
            if (op === 'BLANK') {
                setValue('')
                setValue2('')
                if (inputRef.current) inputRef.current.value = ''
                if (input2Ref.current) input2Ref.current.value = ''
                updateFilter('BLANK', '', '')
                return
            }
            const isDate = fieldType === 'date'
            const switchedToDate = isDate && op !== 'CONTAINS' && operator === 'CONTAINS'
            const switchedFromDate = isDate && op === 'CONTAINS' && operator !== 'CONTAINS'
            const v1 = switchedToDate || switchedFromDate ? '' : value
            if (switchedToDate || switchedFromDate) {
                setValue('')
                if (inputRef.current) inputRef.current.value = ''
            }
            const newValue2 = op === 'BETWEEN' ? value2 : ''
            if (op !== 'BETWEEN') {
                setValue2('')
                if (input2Ref.current) input2Ref.current.value = ''
            }
            updateFilter(op, v1, newValue2)
        },
        [value, value2, operator, fieldType, updateFilter]
    )

    const onValueChange = useCallback(
        (evt) => {
            const v = evt.target.value
            setValue(v)
            updateFilter(operator, v, value2)
        },
        [operator, value2, updateFilter]
    )

    const onValue2Change = useCallback(
        (evt) => {
            const v = evt.target.value
            setValue2(v)
            updateFilter(operator, value, v)
        },
        [operator, value, updateFilter]
    )

    return (
        <TableCell
            sx={{px: 1, py: 0, width: column.width, display: 'flex', flexDirection: 'column'}}
        >
            <FilterOperatorSelectMUI
                fieldType={fieldType}
                value={operator}
                onChange={onOperatorChange}
            />
            <FormControl variant="outlined" size="small" margin="dense" fullWidth>
                <OutlinedInput
                    sx={{
                        fontSize: '0.9rem',
                        '&.Mui-disabled': { backgroundColor: '#f5f5f5' },
                    }}
                    inputProps={{
                        style: {height: '1.1375em'},
                        max: "9999-12-31"
                    }}
                    inputRef={inputRef}
                    type={searchType}
                    disabled={operator === 'BLANK'}
                    onChange={onValueChange}
                    endAdornment={
                        <InputAdornment position='end' >
                            <IconButton edge="end" size="small" onClick={clearFilter} sx={{ p: '2px', color: '#9e9e9e' }}>
                                <FaTimes size={12} />
                            </IconButton>
                        </InputAdornment>
                    }
                />
            </FormControl>
            {operator === 'BETWEEN' && (
                <FormControl variant="outlined" size="small" margin="dense" fullWidth>
                    <OutlinedInput
                        sx={{ fontSize: '0.9rem'}}
                        inputProps={{
                            style: {height: '1.1375em'},
                            max: "9999-12-31"
                        }}
                        inputRef={input2Ref}
                        type={searchType}
                        onChange={onValue2Change}
                        placeholder="End value..."
                    />
                </FormControl>
            )}
        </TableCell>
    )
}

export default SearchField
